import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

import { alignFacilityHeader, inspectFacility } from './pdfFacility.js';
import { requireHolterReport } from './pdfReportType.js';
import { inspectSummary } from './pdfSummary.js';
import { cleanFacility } from './repText.js';

const timestamp = /^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{2}:\d{2}:\d{2}$/;
const isHr = (lines) => lines.some((line) => /^HR\s*-\s*Study ID:/i.test(line.sentence.trim()));
const isIndex = (lines) =>
  lines.some((line) => /^Strip Index(?: \(continued\))?$/i.test(line.sentence.trim()));
// Clean extraction artifacts without removing meaningful punctuation or accented names.
export function cleanStripText(value) {
  return Array.from(String(value ?? '').normalize('NFC'))
    .filter((char) => {
      const code = char.codePointAt(0);
      return (
        !(code < 32 && !/\s/.test(char)) &&
        code !== 127 &&
        ![0x200b, 0x200c, 0x200d, 0x2060, 0xfeff, 0xfffd].includes(code)
      );
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}
export function extractStrips(pdf) {
  const strips = [];
  for (let pageIndex = 0; pageIndex < pdf.getPageCount(); pageIndex += 1) {
    const lines = inspectSummary(pdf, pageIndex).lines;
    // Embedded footer objects can contain hidden original strip text. Never index HR/index pages.
    if (isHr(lines) || isIndex(lines)) continue;
    if (!lines.some((line) => /^CH[123]$/.test(line.sentence.trim()))) continue;
    const seen = new Set();
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      const value = cleanStripText(line.sentence);
      if (!timestamp.test(value)) continue;
      const next = lines[i + 1];
      const bpm =
        next && Math.abs(next.y - line.y) <= 3
          ? /^(\d+)\s*BPM$/i.exec(cleanStripText(next.sentence))
          : null;
      // End-of-recording strips can omit BPM. Keep the strip without inventing HR.
      // Titles may occupy multiple consecutive PDF text objects, or be absent.
      // Preserve content order: a split word must not gain an inserted space.
      const fragments = [];
      for (let j = i - 1; j >= 0; j -= 1) {
        const previous = lines[j];
        const text = cleanStripText(previous.sentence);
        if (
          Math.abs(previous.y - line.y) > 3 ||
          timestamp.test(text) ||
          /^(Symptoms:|Activities:|Name:|CH[123]$|RR$|HR$)/i.test(text) ||
          /^\d+(?:\s*BPM)?$/i.test(text)
        )
          break;
        fragments.unshift(previous.sentence);
      }
      const title = cleanStripText(fragments.join('')) || 'Title not entered';
      const key = `${line.y}:${value}:${title}:${bpm?.[1] ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      strips.push({
        timestamp: value.replace(/\s+/g, ' '),
        title,
        hr: bpm ? Number(bpm[1]) : null,
        pageIndex,
        y: line.y,
      });
    }
  }
  // Reading order is source page order, then top-to-bottom; do not reorder by event date.
  return strips
    .sort((a, b) => a.pageIndex - b.pageIndex || b.y - a.y)
    .map((strip, i) => ({ ...strip, order: i + 1 }));
}
function wrap(text, font, size, width) {
  const lines = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    const trial = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(trial, size) <= width) line = trial;
    else {
      if (line) lines.push(line);
      line = '';
      for (const char of word) {
        if (line && font.widthOfTextAtSize(line + char, size) > width) {
          lines.push(line);
          line = '';
        }
        line += char;
      }
    }
  }
  if (line) lines.push(line);
  return lines;
}
export function layoutStripIndex(strips, font) {
  let rows;
  let size;
  for (size = 10; size >= 7; size -= 0.5) {
    rows = strips.map((strip) => {
      const wrapped = wrap(strip.title, font, size, 200);
      const titleLines = wrapped.slice(0, 2);
      if (wrapped.length > 2) {
        let last = titleLines[1];
        while (last && font.widthOfTextAtSize(last + '...', size) > 200) last = last.slice(0, -1);
        titleLines[1] = last.trimEnd() + '...';
      }
      return { ...strip, titleLines };
    });
    rows = rows.map((row) => ({
      ...row,
      height: Math.max(20, row.titleLines.length * (size + 3) + 10),
    }));
    if (rows.reduce((total, row) => total + row.height, 0) <= 600) break;
  }
  if (size < 7) size = 7;
  const pages = [[]];
  let used = 0;
  for (const row of rows) {
    if (used + row.height > 600) {
      pages.push([]);
      used = 0;
    }
    pages[pages.length - 1].push(row);
    used += row.height;
  }
  return { size, pages };
}
export async function renumberReport(pdf) {
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  for (let i = 1; i < pdf.getPageCount(); i += 1) {
    const page = pdf.getPage(i);
    const label = `-${i + 1}-`;
    page.drawRectangle({ x: 280, y: 8, width: 52, height: 16, color: rgb(1, 1, 1) });
    page.drawText(label, {
      x: 306 - font.widthOfTextAtSize(label, 8.35) / 2,
      y: 13.3,
      size: 8.35,
      font,
      color: rgb(0, 0, 0),
    });
  }
}
export async function appendStripIndex(pdf, afterPageIndex, footer = null) {
  for (let i = 0; i < pdf.getPageCount(); i += 1) {
    if (isIndex(inspectSummary(pdf, i).lines))
      throw new Error(
        'This report already contains a Strip Index. Select the copy without an index.'
      );
  }
  const strips = extractStrips(pdf);
  if (!strips.length) return 0;
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const layout = layoutStripIndex(strips, font);
  const insertion = afterPageIndex + 1;
  const headerLines = inspectSummary(pdf, afterPageIndex).lines;
  const facilityLine = headerLines.find((item) => Math.abs(item.y - 761) < 1);
  const facility = inspectFacility(pdf) || cleanFacility(facilityLine?.sentence) || 'HR Study';
  const demographics = headerLines.filter((item) => Math.abs(item.y - 721) < 1);
  const study =
    headerLines
      .find((item) => /^HR\s*-\s*Study ID:/i.test(item.sentence.trim()))
      ?.sentence.replace(/^HR/, 'Strip Index') || 'Strip Index';
  const centered = (page, text, y, size) => {
    const fitted = Math.min(size, 510 / font.widthOfTextAtSize(text, 1));
    page.drawText(text, {
      x: (612 - font.widthOfTextAtSize(text, fitted)) / 2,
      y,
      size: fitted,
      font,
    });
  };
  for (let n = 0; n < layout.pages.length; n += 1) {
    const page = pdf.insertPage(insertion + n, [612, 792]);
    page.drawRectangle({
      x: 36,
      y: 738,
      width: 540,
      height: 36,
      color: rgb(229 / 255, 239 / 255, 1),
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.45,
    });
    centered(page, facility, 761, 11);
    centered(page, n ? 'Strip Index (continued)' : 'Strip Index', 745, 11);
    page.drawRectangle({
      x: 36,
      y: 716,
      width: 540,
      height: 16,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.45,
    });
    demographics.forEach((item) =>
      page.drawText(item.sentence, { x: item.x, y: 721, size: item.fontSize, font })
    );
    page.drawRectangle({
      x: 36,
      y: 692,
      width: 540,
      height: 18,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.45,
    });
    centered(page, study, 698, 8);
    const columns = [
      ['Order', 40],
      ['Timestamp', 84],
      ['Classification', 240],
      ['HR', 454],
      ['Page', 535],
    ];
    columns.forEach(([text, x]) => page.drawText(text, { x, y: 677, size: 10, font: bold }));
    page.drawLine({
      start: { x: 36, y: 669 },
      end: { x: 576, y: 669 },
      thickness: 0.7,
      color: rgb(0.7, 0.7, 0.7),
    });
    let top = 664;
    for (const row of layout.pages[n]) {
      const y = top - layout.size - 5;
      const number = row.pageIndex + 1 + (row.pageIndex >= insertion ? layout.pages.length : 0);
      const values = [
        [String(row.order), 40],
        [row.timestamp, 84],
        [Number.isFinite(row.hr) ? `${row.hr} BPM` : 'Not provided', 454],
        [String(number), 535],
      ];
      values.forEach(([text, x]) => page.drawText(text, { x, y, size: layout.size, font }));
      row.titleLines.forEach((text, i) =>
        page.drawText(text, { x: 240, y: y - i * (layout.size + 3), size: layout.size, font })
      );
      top -= row.height;
      page.drawLine({
        start: { x: 36, y: top },
        end: { x: 576, y: top },
        thickness: 0.4,
        color: rgb(0.85, 0.85, 0.85),
      });
    }
    if (footer) page.drawPage(footer, { x: 0, y: 0, width: 612, height: 24 });
  }
  return layout.pages.length;
}
export async function insertStripIndex(reportBytes) {
  const pdf = await PDFDocument.load(reportBytes);
  requireHolterReport(pdf);
  const hrPages = [];
  for (let i = 0; i < pdf.getPageCount(); i += 1) {
    const page = pdf.getPage(i);
    if (isIndex(inspectSummary(pdf, i).lines))
      throw new Error(
        'This report already contains a Strip Index. Select the copy without an index.'
      );
    if (
      Math.abs(page.getWidth() - 612) > 0.5 ||
      Math.abs(page.getHeight() - 792) > 0.5 ||
      page.getRotation().angle !== 0
    )
      throw new Error('Strip Index supports unrotated Letter-size reports only.');
    if (isHr(inspectSummary(pdf, i).lines)) hrPages.push(i);
  }
  if (hrPages.length !== 1)
    throw new Error('Select a PDF containing exactly one generated HR Trend page.');
  if (!extractStrips(pdf).length) return { bytes: null, indexPages: 0 };
  const source = await PDFDocument.load(reportBytes);
  await alignFacilityHeader(pdf, source, hrPages[0]);
  const footer = await pdf.embedPage(source.getPage(hrPages[0]), {
    left: 0,
    bottom: 0,
    right: 612,
    top: 24,
  });
  const indexPages = await appendStripIndex(pdf, hrPages[0], footer);
  await renumberReport(pdf);
  return { bytes: await pdf.save(), indexPages };
}
